import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import {
  BufferAttribute,
  BufferGeometry,
  DirectionalLight,
  Line as ThreeLine,
  LineBasicMaterial,
  Object3D,
  Vector3 as ThreeVector3,
} from "three";
import { useSimulation } from "../simulation/SimulationProvider";
import type { PhaseTransition } from "../simulation/state";
import type { Vector3 } from "../simulation/types";
import { CameraRig } from "./CameraRig";
import { Earth } from "./Earth";
import { EarthViewPass } from "./EarthViewPass";
import { Moon } from "./Moon";
import { OrbitGuides } from "./OrbitGuides";
import { EclipseNodes } from "./EclipseNodes";
import type { SceneQuality } from "./SceneCanvas";
import { Sun } from "./Sun";
import { SunlightRays } from "./SunlightRays";
import { getTourPose } from "./tour";

function seededStars(count: number, seed: number): Float32Array {
  const random = () => {
    seed = (1664525 * seed + 1013904223) >>> 0;
    return seed / 0x1_0000_0000;
  };
  const positions = new Float32Array(count * 3);

  for (let index = 0; index < count; index += 1) {
    const azimuth = random() * Math.PI * 2;
    const cosinePolar = random() * 2 - 1;
    const sinePolar = Math.sqrt(1 - cosinePolar * cosinePolar);
    const radius = 32 + random() * 15;
    positions[index * 3] = radius * sinePolar * Math.cos(azimuth);
    positions[index * 3 + 1] = radius * cosinePolar;
    positions[index * 3 + 2] = radius * sinePolar * Math.sin(azimuth);
  }

  return positions;
}

function StarLayer({
  count,
  seed,
  size,
  opacity,
}: {
  readonly count: number;
  readonly seed: number;
  readonly size: number;
  readonly opacity: number;
}) {
  const geometry = useMemo(() => {
    const nextGeometry = new BufferGeometry();
    nextGeometry.setAttribute(
      "position",
      new BufferAttribute(seededStars(count, seed), 3),
    );
    return nextGeometry;
  }, [count, seed]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <points geometry={geometry}>
      <pointsMaterial
        color="#dbe8ff"
        depthWrite={false}
        opacity={opacity}
        size={size}
        sizeAttenuation
        transparent
      />
    </points>
  );
}

function Stars({ quality }: { readonly quality: SceneQuality }) {
  const full = quality === "full";
  return (
    <>
      <StarLayer
        count={full ? 230 : 105}
        seed={0x4d4f4f4e}
        size={full ? 0.06 : 0.085}
        opacity={0.55}
      />
      <StarLayer
        count={full ? 90 : 45}
        seed={0x5354_4152}
        size={full ? 0.12 : 0.15}
        opacity={0.9}
      />
    </>
  );
}

function Sunlight() {
  const light = useRef<DirectionalLight>(null);
  const target = useRef<Object3D>(null);

  useEffect(() => {
    if (light.current !== null && target.current !== null) {
      light.current.target = target.current;
      target.current.updateMatrixWorld();
    }
  }, []);

  useFrame(() => light.current?.target.updateMatrixWorld());

  return (
    <>
      <directionalLight
        ref={light}
        color="#fff8ef"
        intensity={3.2}
        position={[-22, 0, 0]}
      />
      <object3D ref={target} position={[0, 0, 0]} />
    </>
  );
}

// Brief Earth→Moon sight-line pulse that fades out as a phase transition completes.
function SightLine({
  moonPosition,
  transition,
}: {
  readonly moonPosition: Vector3;
  readonly transition: PhaseTransition | null;
}) {
  const geometry = useMemo(() => new BufferGeometry(), []);
  const line = useMemo(
    () =>
      new ThreeLine(
        geometry,
        new LineBasicMaterial({
          color: "#8fd0ff",
          transparent: true,
          depthWrite: false,
        }),
      ),
    [geometry],
  );

  useEffect(
    () => () => {
      geometry.dispose();
      line.material.dispose();
    },
    [geometry, line.material],
  );

  useEffect(() => {
    geometry.setFromPoints([
      new ThreeVector3(0, 0, 0),
      new ThreeVector3(...moonPosition),
    ]);
  }, [geometry, moonPosition]);

  if (transition === null) {
    return null;
  }

  const progress = transition.elapsedSeconds / transition.durationSeconds || 0;
  line.material.opacity = 0.75 * (1 - progress);

  return <primitive object={line} />;
}

export function SystemScene({
  earthViewTargetSelector,
  quality,
  tourStep = null,
}: {
  readonly earthViewTargetSelector: string;
  readonly quality: SceneQuality;
  readonly tourStep?: number | null;
}) {
  const { snapshot, state, selectPhase } = useSimulation();
  const tourPose =
    tourStep === null ? null : getTourPose(tourStep, snapshot.moonPosition);

  return (
    <>
      <color attach="background" args={["#030812"]} />
      <Stars quality={quality} />
      <Sunlight />
      {/* Cool sky / warm ecliptic fill keeps night sides readable as spheres. */}
      <hemisphereLight
        args={["#9ec5ff", "#1a140c", 0.28]}
        position={[0, 8, 0]}
      />
      <ambientLight color="#2a3548" intensity={0.06} />
      <Sun classroomMode={state.guides.classroomMode} />
      {state.guides.sunlightRays ? <SunlightRays /> : null}
      {state.guides.eclipseNodes ? <EclipseNodes /> : null}
      <OrbitGuides
        activePhaseIndex={snapshot.phaseIndex}
        showAllPhasePositions={state.guides.allPhasePositions}
        showOrbitalPlanes={state.guides.orbitalPlanes}
        showPhaseLabels={state.guides.phaseLabels}
        selectPhase={(phaseIndex) => selectPhase(phaseIndex, "shortest")}
      />
      <Earth
        classroomMode={state.guides.classroomMode}
        isPlaying={state.isPlaying}
        showRotation={state.guides.earthRotation}
        speed={state.speed}
      />
      <Moon
        classroomMode={state.guides.classroomMode}
        showRotationMarker={state.guides.moonRotationMarker}
        showTerminator={state.guides.moonTerminator}
        snapshot={snapshot}
      />
      <SightLine
        moonPosition={snapshot.moonPosition}
        transition={state.transition}
      />
      <CameraRig
        cameraResetToken={state.cameraResetToken}
        tourPose={tourPose}
      />
      <EarthViewPass
        snapshot={snapshot}
        targetSelector={earthViewTargetSelector}
      />
    </>
  );
}
